import { useCallback, useEffect, useRef, useState } from 'react';
import { Scenario } from '../../types';

const MAX_HISTORY = 50;

export interface InlineScenarioChangeOptions {
  /** Snapshot scenario before add/remove of a content block or nested sub-block. */
  recordBlockHistory?: boolean;
}

export type InlineScenarioChange = (
  scenario: Scenario,
  options?: InlineScenarioChangeOptions
) => void;

function cloneScenario(scenario: Scenario): Scenario {
  return structuredClone(scenario);
}

export function isInlineEditorTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export function useInlineBlockHistory(historyKey: string) {
  const undoStack = useRef<Scenario[]>([]);
  const redoStack = useRef<Scenario[]>([]);
  const [revision, setRevision] = useState(0);

  const bump = useCallback(() => setRevision((n) => n + 1), []);

  useEffect(() => {
    undoStack.current = [];
    redoStack.current = [];
    bump();
  }, [historyKey, bump]);

  const reset = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    bump();
  }, [bump]);

  const pushSnapshot = useCallback(
    (scenario: Scenario) => {
      undoStack.current.push(cloneScenario(scenario));
      if (undoStack.current.length > MAX_HISTORY) {
        undoStack.current.shift();
      }
      redoStack.current = [];
      bump();
    },
    [bump]
  );

  const undo = useCallback(
    (current: Scenario): Scenario | null => {
      const previous = undoStack.current.pop();
      if (!previous) return null;
      redoStack.current.push(cloneScenario(current));
      bump();
      return cloneScenario(previous);
    },
    [bump]
  );

  const redo = useCallback(
    (current: Scenario): Scenario | null => {
      const next = redoStack.current.pop();
      if (!next) return null;
      undoStack.current.push(cloneScenario(current));
      bump();
      return cloneScenario(next);
    },
    [bump]
  );

  void revision;

  return {
    pushSnapshot,
    undo,
    redo,
    reset,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
  };
}
